/**
 * Calculates the distance between two points on Earth using the Haversine formula.
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};

/**
 * Returns the international calling code for a given ISO 3166-1 alpha-2 country code.
 */
export const getCountryCallingCode = (countryCode) => {
  const codes = {
    PH: '63',
    US: '1',
    GB: '44',
    AU: '61',
    CA: '1',
    SG: '65',
    AE: '971',
    SA: '966',
    JP: '81',
    KR: '82',
    CN: '86',
    IN: '91'
  };
  return codes[countryCode] || null;
};

/**
 * Simple parser for OpenStreetMap opening_hours tag
 * Returns {isOpen: boolean | null, statusText: string}
 */
export const checkOpeningStatus = (openingHours) => {
  if (!openingHours) return { isOpen: null, statusText: 'Hours not listed' };
  if (openingHours === '24/7') return { isOpen: true, statusText: 'Open 24/7' };

  try {
    const now = new Date();
    const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Basic support for "Mo-Fr 08:00-18:00" format
    const parts = openingHours.split(';');
    for (let part of parts) {
      part = part.trim();
      if (part.includes(currentDay) || part.includes('Mo-Su') || part.includes('24/7')) {
        const timeMatch = part.match(/(\d{2}):(\d{2})-(\d{2}):(\d{2})/);
        if (timeMatch) {
          const start = parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2]);
          const end = parseInt(timeMatch[3]) * 60 + parseInt(timeMatch[4]);

          if (currentTime >= start && currentTime <= end) {
            return { isOpen: true, statusText: `Open until ${timeMatch[3]}:${timeMatch[4]}` };
          } else {
            return { isOpen: false, statusText: `Closed • Opens ${timeMatch[1]}:${timeMatch[2]}` };
          }
        }
      }
    }
  } catch (e) {
    console.warn('Hours parsing failed', e);
  }
  return { isOpen: null, statusText: openingHours };
};

export const getFullAddress = (tags) => {
  const parts = [
    tags['addr:housenumber'],
    tags['addr:street'],
    tags['addr:suburb'],
    tags['addr:city'] || tags['addr:town']
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(',') : (tags['addr:full'] || 'Address not listed');
};